/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Decides which access-change surface the person sees, and keeps their claims
 * in step with their grant.
 */

import React from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "@lemonadetraining/web-shared";
import { useUserAccess } from "../../hooks/useUserAccess";
import { useGrantRefresh } from "../../hooks/useGrantRefresh";
import { useAccessNotices } from "../../hooks/useAccessNotices";
import RoleChangeBanner from "./RoleChangeBanner";
import AccessRemovedPage from "./AccessRemovedPage";
import { surfaceOf } from "./accessNoticeCopy";
import type { StoredNotice } from "./accessNoticeCopy";

/*
 * Routes a demoted admin may genuinely no longer use, and who still can.
 * `box_coach` really does hold `view_box_portal`, so telling a coach who lost
 * only the admin bit that the Business Portal is gone would be false.
 */
const ROUTE_STILL_USABLE_BY: Array<{ path: string; coachKeeps: boolean }> = [
  { path: "/box-portal", coachKeeps: true },
  { path: "/requests", coachKeeps: false },
  { path: "/gym-settings", coachKeeps: false },
  { path: "/team", coachKeeps: false },
];

const AccessNoticesHost: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useGrantRefresh();
  useAccessNotices();

  const { pathname } = useLocation();
  const { isBoxAdmin, isBoxCoach, isLoading, boxID } = useUserAccess();
  const unread: StoredNotice[] = useAppSelector((state) => state.accessNotices.unread);

  const removal = unread.find((notice) => surfaceOf(notice.kind) === "modal");
  const demotion = unread.find(
    (notice) => notice.kind === "demoted_admin" && notice.boxID === boxID
  );

  const lostThisRoute = ROUTE_STILL_USABLE_BY.some(
    ({ path, coachKeeps }) => pathname.includes(path) && !(coachKeeps && isBoxCoach)
  );
  // Only once the demotion has actually landed in the claims: while they are
  // still resolving the person keeps the page they are on.
  const demotedPage =
    demotion && lostThisRoute && !isBoxAdmin && !isLoading ? (
      <AccessRemovedPage notice={demotion} variant="demoted" isBoxCoach={isBoxCoach} />
    ) : null;

  return (
    <>
      {removal && <AccessRemovedPage notice={removal} variant="removed" isBoxCoach={isBoxCoach} />}
      <RoleChangeBanner />
      {demotedPage ?? children}
    </>
  );
};

export default AccessNoticesHost;
